import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { StatusChip } from '@/components/ui/Chip';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { TYPE } from '@/lib/typography';
import { uiDateLocale } from '@/lib/i18n';
import { cn } from '@/utils/cn';
import {
  usePatientCommunicationPreference,
  useSetCommunicationPreference,
  type CommunicationServiceChannel,
} from '@/hooks/useConsentRecords';
import { STAFF_RECORD_METHODS, type ConsentRecordMethod } from '@/lib/consent/consentTypes';
import { resolveCommunicationPreference, type LegacySmsConsent } from '@/lib/consent/communicationPreference';

const CHANNELS: CommunicationServiceChannel[] = ['sms', 'email', 'portal_only'];

export function CommunicationPreferenceCard({
  patientId,
  clinicId,
  canEdit,
  legacySmsConsent,
}: {
  patientId: string;
  clinicId?: string | null;
  canEdit: boolean;
  legacySmsConsent: LegacySmsConsent;
}) {
  const { t } = useTranslation();
  const query = usePatientCommunicationPreference(patientId, clinicId);
  const save = useSetCommunicationPreference();
  const resolved = resolveCommunicationPreference(query.data ?? null, legacySmsConsent);

  const [editing, setEditing] = useState(false);
  const [channel, setChannel] = useState<CommunicationServiceChannel>(resolved.preferredChannel);
  const [smsOptOut, setSmsOptOut] = useState(resolved.smsOptOut);
  const [emailOptOut, setEmailOptOut] = useState(resolved.emailOptOut);
  const [method, setMethod] = useState<ConsentRecordMethod>('oral');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (editing) return;
    setChannel(resolved.preferredChannel);
    setSmsOptOut(resolved.smsOptOut);
    setEmailOptOut(resolved.emailOptOut);
  }, [editing, resolved.preferredChannel, resolved.smsOptOut, resolved.emailOptOut]);

  const handleSave = async () => {
    try {
      await save.mutateAsync({
        p_patient_id: patientId,
        p_preferred_channel: channel,
        p_sms_opt_out: smsOptOut,
        p_email_opt_out: emailOptOut,
        p_method: method,
        ...(note.trim() ? { p_note: note.trim() } : {}),
      });
      toast.success(t('consent.communication.saved'));
      setEditing(false);
      setNote('');
    } catch (err) {
      console.error('set_communication_preference failed', err);
      toast.error(t('consent.communication.save_error'));
    }
  };

  return (
    <section className="rounded-card border border-ds-line p-s4" aria-labelledby="consent-communication-title">
      <div className="flex flex-wrap items-start gap-s3">
        <div className="min-w-0 flex-1">
          <h3 id="consent-communication-title" className={cn(TYPE.section, 'text-ds-text')}>
            {t('consent.communication.title')}
          </h3>
          <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>{t('consent.communication.description')}</p>
        </div>
        {canEdit && !editing ? (
          <Button type="button" variant="secondary" onClick={() => setEditing(true)}>
            {t('consent.communication.edit')}
          </Button>
        ) : null}
      </div>

      {editing ? (
        <div className="mt-s4 space-y-s4">
          <Select
            id="consent-communication-channel"
            label={t('consent.communication.preferred')}
            value={channel}
            onChange={(e) => setChannel(e.target.value as CommunicationServiceChannel)}
          >
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {t(`consent.communication.channel.${c}`)}
              </option>
            ))}
          </Select>
          <Checkbox
            id="consent-communication-sms-opt-out"
            checked={smsOptOut}
            onChange={(e) => setSmsOptOut(e.target.checked)}
            label={t('consent.communication.sms_opt_out')}
          />
          <Checkbox
            id="consent-communication-email-opt-out"
            checked={emailOptOut}
            onChange={(e) => setEmailOptOut(e.target.checked)}
            label={t('consent.communication.email_opt_out')}
          />
          <Select
            id="consent-communication-method"
            label={t('consent.fields.method')}
            value={method}
            onChange={(e) => setMethod(e.target.value as ConsentRecordMethod)}
          >
            {STAFF_RECORD_METHODS.map((m) => (
              <option key={m} value={m}>
                {t(`consent.method.${m}`)}
              </option>
            ))}
          </Select>
          <Textarea
            id="consent-communication-note"
            label={`${t('consent.fields.note')} (${t('common.optional')})`}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex justify-end gap-s2">
            <Button type="button" variant="tertiary" onClick={() => setEditing(false)} disabled={save.isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={() => void handleSave()} loading={save.isPending}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      ) : (
        <dl className="mt-s4 grid grid-cols-1 gap-s3 nav:grid-cols-3">
          <div>
            <dt className={cn(TYPE.small, 'text-ds-muted')}>{t('consent.communication.preferred')}</dt>
            <dd className={cn(TYPE.body, 'mt-s1 text-ds-text')}>
              {t(`consent.communication.channel.${resolved.preferredChannel}`)}
            </dd>
          </div>
          <div>
            <dt className={cn(TYPE.small, 'text-ds-muted')}>{t('consent.communication.sms')}</dt>
            <dd className="mt-s1">
              <StatusChip tone={resolved.smsOptOut ? 'neutral' : 'completed'}>
                {resolved.smsOptOut ? t('consent.communication.opted_out') : t('consent.communication.allowed')}
              </StatusChip>
            </dd>
          </div>
          <div>
            <dt className={cn(TYPE.small, 'text-ds-muted')}>{t('consent.communication.email')}</dt>
            <dd className="mt-s1">
              <StatusChip tone={resolved.emailOptOut ? 'neutral' : 'completed'}>
                {resolved.emailOptOut ? t('consent.communication.opted_out') : t('consent.communication.allowed')}
              </StatusChip>
            </dd>
          </div>
          <p className={cn(TYPE.small, 'text-ds-muted nav:col-span-3')}>
            {resolved.source === 'preference' && resolved.setAt
              ? t('consent.communication.set_at', {
                  date: new Date(resolved.setAt).toLocaleDateString(uiDateLocale(), {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  }),
                  method: resolved.method ? t(`consent.method.${resolved.method}`) : '—',
                })
              : resolved.source === 'legacy_sms_consent'
                ? t('consent.communication.legacy_sms')
                : t('consent.communication.default')}
          </p>
        </dl>
      )}
    </section>
  );
}
